import React from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import './DashboardPageSkeleton.css';

/**
 * Mirrors the exact DOM structure of DashboardPage:
 *
 *  1. DashboardMetrics  → Row g-4 > 4× Col xxl-3 > Card.dashboard_metrics
 *  2. DashboardAnalytics → Row g-4 > Col xxl-5 (CreditBalanceCard) + Col xxl-7 (PitchAnalyticsCard)
 *  3. Bottom row        → Col xxl-8 (TransactionDetailTable) + Col xxl-4 (NewConnectionsDasboard)
 */
const DashboardPageSkeleton: React.FC = () => (
  <div className="dashboard_details">

    {/* ── 1. Metric Cards — same as DashboardMetrics isLoading skeleton ── */}
    <Row className="g-4">
      {[...Array(4)].map((_, i) => (
        <Col key={i} xxl={3} xl={3} lg={6} md={6} xs={12} sm={12} className="mb-4">
          <Card className="border-0 h-100 dashboard_metrics">
            <Card.Body className="d-flex p-3">
              {/* Left colored bar */}
              <div className="me-3" style={{ width: 6, borderRadius: 10, height: 86, overflow: 'hidden' }}>
                <Skeleton width={6} height={86} borderRadius={10} />
              </div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    {/* title */}
                    <Skeleton width="60%" height={14} borderRadius={6} style={{ marginBottom: 6 }} />
                    {/* value */}
                    <Skeleton width="40%" height={28} borderRadius={6} style={{ marginBottom: 8 }} />
                  </div>
                  {/* icon box */}
                  <Skeleton width={38} height={38} borderRadius={8} style={{ marginLeft: 12, flexShrink: 0 }} />
                </div>
                {/* change row */}
                <Skeleton width="55%" height={14} borderRadius={6} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>

    {/* ── 2. Analytics Row — same as DashboardAnalytics py-4 wrapper ── */}
    <div className="py-4">
      <Row className="g-4">

        {/* CreditBalanceCard — Col xxl-5 xl-5 */}
        <Col xxl={5} xl={5} lg={12} md={12} sm={12}>
          <Card className="h-100 border-0 p-4">
            {/* "Your Credit Balance" heading */}
            <Skeleton width={180} height={22} borderRadius={6} style={{ marginBottom: 12 }} />
            <div className="border-top mb-3" />
            <Card.Body className="p-0">
              <Row className="align-items-center">
                {/* Donut chart — Col xs-12 lg-8 text-center */}
                <Col xs={12} lg={8} className="text-center mb-4 mb-lg-0">
                  <div
                    className="mx-auto position-relative d-flex align-items-center justify-content-center"
                    style={{ width: '100%', maxWidth: 280, aspectRatio: '1/1' }}
                  >
                    <Skeleton circle width={200} height={200} />
                    {/* Center label */}
                    <div
                      className="position-absolute d-flex flex-column align-items-center justify-content-center bg-white rounded-circle"
                      style={{ width: '53%', height: '53%' }}
                    >
                      <Skeleton width={40} height={28} borderRadius={6} style={{ marginBottom: 4 }} />
                      <Skeleton width={70} height={12} borderRadius={6} />
                    </div>
                  </div>
                </Col>
                {/* Metrics list — Col xs-12 lg-4 */}
                <Col xs={12} lg={4}>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="d-flex align-items-center justify-content-between flex-lg-column mb-3">
                      <Skeleton width={80} height={13} borderRadius={6} />
                      <div className="d-flex align-items-center" style={{ marginTop: 4 }}>
                        <Skeleton circle width={10} height={10} style={{ marginRight: 8 }} />
                        <Skeleton width={36} height={22} borderRadius={6} />
                      </div>
                    </div>
                  ))}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* PitchAnalyticsCard — Col xxl-7 xl-7 */}
        <Col xxl={7} xl={7} lg={12} md={12} sm={12}>
          <div className="container p-0">
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-body p-4">
                {/* Header: icon+title · period buttons */}
                <div className="d-flex justify-content-between align-items-center gap-3 mb-4 pb-3 border-bottom flex-wrap">
                  <div className="d-flex align-items-center gap-2">
                    <Skeleton circle width={20} height={20} />
                    <Skeleton width={140} height={22} borderRadius={6} />
                  </div>
                  <div className="d-flex gap-2 flex-nowrap">
                    <Skeleton width={50} height={32} borderRadius={6} />
                    <Skeleton width={58} height={32} borderRadius={6} />
                    <Skeleton width={66} height={32} borderRadius={6} />
                  </div>
                </div>
                {/* Chart area — 300px same as ResponsiveContainer */}
                <div style={{ height: 300 }}>
                  <Skeleton width="100%" height={300} borderRadius={8} />
                </div>
              </div>
            </div>
          </div>
        </Col>

      </Row>
    </div>

    {/* ── 3. Bottom Row ── */}
    <div className="row">

      {/* TransactionDetailTable — Col xxl-8 xl-7 */}
      <div className="col-xxl-8 col-xl-7 col-lg-12 col-md-12 col-sm-12 mb-3">
        <Card className="shadow-sm border-0 rounded-4">
          <Card.Body>
            {/* Tabs row — "Transactions" | "Credits" */}
            <div style={{ borderBottom: '1px solid #eaeaea', marginBottom: 15 }}>
              <div className="d-flex gap-3 mb-4">
                <Skeleton width={100} height={16} borderRadius={6} />
                <Skeleton width={70} height={16} borderRadius={6} />
              </div>
            </div>
            {/* Table header */}
            <div className="d-flex gap-3 py-2" style={{ borderBottom: '1px solid #f0f0f0' }}>
              <Skeleton width={18} height={18} borderRadius={4} />
              {[80, 90, 120, 70, 60, 56].map((w, i) => (
                <Skeleton key={i} width={w} height={13} borderRadius={6} />
              ))}
            </div>
            {/* Table rows */}
            {[...Array(3)].map((_, i) => (
              <div key={i} className="d-flex align-items-center gap-3 py-3" style={{ borderBottom: '1px solid #f8f8f8' }}>
                <Skeleton width={18} height={18} borderRadius={4} style={{ flexShrink: 0 }} />
                <Skeleton width={80} height={13} borderRadius={6} />
                <Skeleton width={90} height={13} borderRadius={6} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <Skeleton width={80} height={13} borderRadius={6} />
                  <Skeleton width={50} height={11} borderRadius={6} />
                </div>
                <Skeleton width={65} height={24} borderRadius={999} />
                <Skeleton width={50} height={13} borderRadius={6} />
                <div className="d-flex gap-2">
                  <Skeleton circle width={16} height={16} />
                  <Skeleton circle width={16} height={16} />
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>
      </div>

      {/* NewConnectionsDasboard — Col xxl-4 xl-5 */}
      <div className="col-xxl-4 col-xl-5 col-lg-12 col-md-12 col-sm-12 mb-3">
        <Card className="bg-white shadow-sm border-0 rounded-4">
          <Card.Body>
            {/* "New Connections" heading */}
            <Skeleton width={160} height={22} borderRadius={6} style={{ marginBottom: 12 }} />
            <div className="border-top mb-3" />
            {/* 4 connection cards — mirrors NewConnections.tsx structure */}
            {[...Array(4)].map((_, i) => (
              <div key={i} className="mb-2 rounded-4 candidation_profile_cards">
                <div className="d-flex align-items-start">
                  {/* Avatar + progress ring */}
                  <div style={{ position: 'relative', flexShrink: 0, width: 56, height: 68 }}>
                    <Skeleton circle width={52} height={52} style={{ position: 'absolute', top: 0, left: 0 }} />
                    <Skeleton
                      circle
                      width={31}
                      height={31}
                      style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}
                    />
                  </div>
                  {/* Info */}
                  <div className="ms-3 flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <Skeleton width={120} height={16} borderRadius={6} />
                      <Skeleton circle width={17} height={17} />
                    </div>
                    <Skeleton width={140} height={13} borderRadius={6} style={{ marginBottom: 6 }} />
                    <div className="d-flex gap-3">
                      <Skeleton width={50} height={13} borderRadius={6} />
                      <Skeleton width={80} height={13} borderRadius={6} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Card.Body>
        </Card>
      </div>

    </div>
  </div>
);

export default DashboardPageSkeleton;
